/**
 * widget-registry — szew rozszerzalności pulpitu.
 *
 * Jeden wpis per typ widżetu. Siatka robi lookup `WIDGET_REGISTRY[type]` →
 * renderuje `.component`, paleta „dodaj" iteruje po wpisach, edytor configu bierze
 * `.defaultConfig`. Dołożenie odłożonego typu (iter. 2+) = jeden wpis + komponent,
 * zero zmian w siatce/ramie.
 *
 * Każdy typ wskazuje swój komponent renderujący oraz wygląd ramy (nagłówek, podtytuł,
 * przełącznik okresu) wg Figmy „08.6 · Cards & widgets".
 */

import type { ComponentType } from 'react';

import { ActivityFeedWidget } from './widgets/ActivityFeedWidget';
import { AlertsWidget } from './widgets/AlertsWidget';
import { BreakdownWidget } from './widgets/BreakdownWidget';
import { ChartWidget } from './widgets/ChartWidget';
import { CountdownWidget } from './widgets/CountdownWidget';
import { CustomWidget } from './widgets/CustomWidget';
import { FlowWidget } from './widgets/FlowWidget';
import { KpiWidget } from './widgets/KpiWidget';
import { NoteWidget } from './widgets/NoteWidget';
import { PendingApprovalsWidget } from './widgets/PendingApprovalsWidget';
import { ProcessPerformanceWidget } from './widgets/ProcessPerformanceWidget';
import { ProfileWidget } from './widgets/ProfileWidget';
import { QuickActionsWidget } from './widgets/QuickActionsWidget';
import { RecentRecordsWidget } from './widgets/RecentRecordsWidget';
import { TransactionsWidget } from './widgets/TransactionsWidget';
import type { DashboardWidget, WidgetRenderProps, WidgetType } from './types';
import type { WidgetHeaderStyle } from './WidgetShell';

type WidgetConfigOf<T extends WidgetType> = Extract<DashboardWidget, { type: T }>['config'];

/** Kontrolka okresu w nagłówku: lista rozwijana albo przełącznik segmentowy (Tydzień | Miesiąc | Rok). */
export type PeriodUI = 'none' | 'dropdown' | 'segmented';

export interface WidgetRegistryEntry<T extends WidgetType = WidgetType> {
  component: ComponentType<WidgetRenderProps>;
  /** Klucz i18n etykiety w palecie „dodaj". */
  labelKey: string;
  /** Klucz i18n domyślnego tytułu kafelka. */
  defaultTitleKey: string;
  /** Klucz i18n podtytułu pod tytułem (styl nagłówka `title`); brak = bez podtytułu. */
  subtitleKey?: string;
  /** Nazwa ikony z `icon-map`. */
  icon: string;
  /** Wygląd nagłówka kafelka: tytuł+podtytuł, mała etykieta statystyki albo brak. */
  headerStyle: WidgetHeaderStyle;
  /** Fabryka domyślnego configu nowej instancji. */
  defaultConfig: () => WidgetConfigOf<T>;
  /** Domyślny rozmiar na siatce (lg). */
  defaultSize: { w: number; h: number; minW: number; minH: number };
  /** Czy kafelek pokazuje filtr okresu (KPI + wykresy). */
  supportsPeriod: boolean;
  /** Rodzaj kontrolki okresu; przy `supportsPeriod: true` domyślnie lista rozwijana. */
  periodUI?: PeriodUI;
  /** Okres startowy, gdy user nie wybrał innego (nadpisuje domyślny siatki). */
  defaultPeriod?: 'all' | 'year' | 'month' | 'week' | 'today';
}

export const WIDGET_REGISTRY: { [T in WidgetType]: WidgetRegistryEntry<T> } = {
  kpi: {
    component: KpiWidget,
    labelKey: 'tenant.dashboard.widgets.kpi.label',
    defaultTitleKey: 'tenant.dashboard.widgets.kpi.defaultTitle',
    icon: 'BadgeCheck',
    headerStyle: 'stat',
    defaultConfig: () => ({ metric: 'contractors' }),
    defaultSize: { w: 3, h: 2, minW: 2, minH: 2 },
    supportsPeriod: true,
    periodUI: 'dropdown',
    defaultPeriod: 'all',
  },
  pending_approvals: {
    component: PendingApprovalsWidget,
    labelKey: 'tenant.dashboard.widgets.pending_approvals.label',
    defaultTitleKey: 'tenant.dashboard.widgets.pending_approvals.defaultTitle',
    icon: 'ShieldCheck',
    headerStyle: 'title',
    defaultConfig: () => ({}),
    defaultSize: { w: 5, h: 5, minW: 3, minH: 3 },
    supportsPeriod: false,
  },
  recent_records: {
    component: RecentRecordsWidget,
    labelKey: 'tenant.dashboard.widgets.recent_records.label',
    defaultTitleKey: 'tenant.dashboard.widgets.recent_records.defaultTitle',
    icon: 'ScrollText',
    headerStyle: 'title',
    defaultConfig: () => ({ entity: 'contractors', limit: 5 }),
    defaultSize: { w: 4, h: 4, minW: 3, minH: 3 },
    supportsPeriod: false,
  },
  activity_feed: {
    component: ActivityFeedWidget,
    labelKey: 'tenant.dashboard.widgets.activity_feed.label',
    defaultTitleKey: 'tenant.dashboard.widgets.activity_feed.defaultTitle',
    icon: 'ScrollText',
    headerStyle: 'title',
    defaultConfig: () => ({ limit: 7 }),
    defaultSize: { w: 4, h: 5, minW: 3, minH: 3 },
    supportsPeriod: false,
  },
  chart: {
    component: ChartWidget,
    labelKey: 'tenant.dashboard.widgets.chart.label',
    defaultTitleKey: 'tenant.dashboard.widgets.chart.defaultTitle',
    subtitleKey: 'tenant.dashboard.widgets.chart.subtitle',
    icon: 'LayoutDashboard',
    headerStyle: 'title',
    defaultConfig: () => ({ entity: 'contractors', compare: true }),
    defaultSize: { w: 7, h: 5, minW: 4, minH: 4 },
    supportsPeriod: true,
    periodUI: 'segmented',
    defaultPeriod: 'year',
  },
  breakdown: {
    component: BreakdownWidget,
    labelKey: 'tenant.dashboard.widgets.breakdown.label',
    defaultTitleKey: 'tenant.dashboard.widgets.breakdown.defaultTitle',
    subtitleKey: 'tenant.dashboard.widgets.breakdown.subtitle',
    icon: 'PieChart',
    headerStyle: 'title',
    defaultConfig: () => ({ metric: 'employees' }),
    defaultSize: { w: 5, h: 5, minW: 3, minH: 4 },
    supportsPeriod: true,
    periodUI: 'dropdown',
    defaultPeriod: 'all',
  },
  flow: {
    component: FlowWidget,
    labelKey: 'tenant.dashboard.widgets.flow.label',
    defaultTitleKey: 'tenant.dashboard.widgets.flow.defaultTitle',
    subtitleKey: 'tenant.dashboard.widgets.flow.subtitle',
    icon: 'BarChart3',
    headerStyle: 'title',
    defaultConfig: () => ({}),
    defaultSize: { w: 6, h: 5, minW: 4, minH: 4 },
    supportsPeriod: true,
    periodUI: 'segmented',
    defaultPeriod: 'year',
  },
  process_performance: {
    component: ProcessPerformanceWidget,
    labelKey: 'tenant.dashboard.widgets.process_performance.label',
    defaultTitleKey: 'tenant.dashboard.widgets.process_performance.defaultTitle',
    subtitleKey: 'tenant.dashboard.widgets.process_performance.subtitle',
    icon: 'Activity',
    headerStyle: 'title',
    defaultConfig: () => ({}),
    defaultSize: { w: 5, h: 5, minW: 3, minH: 4 },
    supportsPeriod: true,
    periodUI: 'dropdown',
    defaultPeriod: 'year',
  },
  transactions: {
    component: TransactionsWidget,
    labelKey: 'tenant.dashboard.widgets.transactions.label',
    defaultTitleKey: 'tenant.dashboard.widgets.transactions.defaultTitle',
    icon: 'Receipt',
    headerStyle: 'title',
    defaultConfig: () => ({ limit: 5 }),
    defaultSize: { w: 4, h: 5, minW: 3, minH: 3 },
    supportsPeriod: false,
  },
  alerts: {
    component: AlertsWidget,
    labelKey: 'tenant.dashboard.widgets.alerts.label',
    defaultTitleKey: 'tenant.dashboard.widgets.alerts.defaultTitle',
    icon: 'Bell',
    headerStyle: 'title',
    defaultConfig: () => ({}),
    defaultSize: { w: 4, h: 5, minW: 3, minH: 3 },
    supportsPeriod: false,
  },
  profile: {
    component: ProfileWidget,
    labelKey: 'tenant.dashboard.widgets.profile.label',
    defaultTitleKey: 'tenant.dashboard.widgets.profile.defaultTitle',
    icon: 'User',
    headerStyle: 'none',
    defaultConfig: () => ({}),
    defaultSize: { w: 3, h: 5, minW: 3, minH: 5 },
    supportsPeriod: false,
  },
  quick_actions: {
    component: QuickActionsWidget,
    labelKey: 'tenant.dashboard.widgets.quick_actions.label',
    defaultTitleKey: 'tenant.dashboard.widgets.quick_actions.defaultTitle',
    icon: 'Link',
    headerStyle: 'none',
    defaultConfig: () => ({ actions: [] }),
    defaultSize: { w: 8, h: 2, minW: 3, minH: 2 },
    supportsPeriod: false,
  },
  note: {
    component: NoteWidget,
    labelKey: 'tenant.dashboard.widgets.note.label',
    defaultTitleKey: 'tenant.dashboard.widgets.note.defaultTitle',
    icon: 'FileText',
    headerStyle: 'title',
    defaultConfig: () => ({ html: '' }),
    defaultSize: { w: 4, h: 4, minW: 2, minH: 2 },
    supportsPeriod: false,
  },
  countdown: {
    component: CountdownWidget,
    labelKey: 'tenant.dashboard.widgets.countdown.label',
    defaultTitleKey: 'tenant.dashboard.widgets.countdown.defaultTitle',
    icon: 'Ticket',
    headerStyle: 'title',
    defaultConfig: () => ({}),
    defaultSize: { w: 4, h: 3, minW: 2, minH: 2 },
    supportsPeriod: false,
  },
  // Widżet danych z kreatora. NIE pojawia się w palecie po TYPIE (picker ma osobną sekcję
  // definicji) — stąd `defaultConfig` z pustym `definitionId` to tylko fallback typu; realny
  // config (definitionId + snapshot visualType + tytuł) składa `createCustomWidget`. `defaultSize`
  // to fallback — faktyczny rozmiar dobiera `customWidgetSize(visualType)` przy dodaniu.
  custom: {
    component: CustomWidget,
    labelKey: 'tenant.dashboard.widgets.custom.label',
    defaultTitleKey: 'tenant.dashboard.widgets.custom.defaultTitle',
    icon: 'LayoutDashboard',
    headerStyle: 'title',
    defaultConfig: () => ({ definitionId: '' }),
    defaultSize: { w: 6, h: 4, minW: 3, minH: 3 },
    supportsPeriod: false,
  },
};
